import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOrgNavigate } from "app/routes/useOrgNavigate";
import { ArrowBack } from '@mui/icons-material';
import { Button, CircularProgress, Container, Divider } from '@mui/material';
import { upperFirst } from 'lodash';
import styled from 'styled-components';

import HeaderWithDivider from '@/operator/components/general/headers/HeaderWithDivider';
import useNotification from 'app/contexts/NotificationContext/hooks/useNotification';
import ItemWithSwitch from './ItemWithSwitch';
import { appRoutes } from '@/routes/constants';
import { useDeactivateConnectionMutation } from 'generated/graphql';

export const HeadlineContainer = styled.div`
    justify-content: space-between;
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: center;
    margin-bottom: 15px;
`;

export function ConnectionSettings(): JSX.Element {
    const [searchParams] = useSearchParams();
    const { operatorConnectionEnableTalk, operatorConnectionEnableHelp } =
        useFlags();
    const { t } = useTranslation();
    const routerNavigate = useNavigate();
    const navigate = useOrgNavigate();

    const { showNotification } = useNotification();

    const organizationId = searchParams.get('id');
    const label = searchParams.get('label');

    const [deactivateConnection, { loading, error, data }] =
        useDeactivateConnectionMutation();

    const handleDeleteConnection = () => {
        deactivateConnection({
            variables: {
                organizationId,
            },
        });
    };

    useEffect(() => {
        if (organizationId === null) {
            routerNavigate(-1);
        }
    }, [organizationId]);

    useEffect(() => {
        if (data) {
            showNotification({
                severity: 'success',
                message: t('Notifications.ConnectionDeleted'),
            });
            navigate(
                `${appRoutes.OPERATOR_ADMIN_CONNECTIONS}?notification=connectionDelete`,
            );
        }
    }, [data]);

    useEffect(() => {
        if (error) {
            showNotification({
                message: t('Notifications.ConnectionDeletedFailed'),
                severity: 'error',
            });
        }
    }, [error]);

    const isZendesk = label === 'zendesk';

    return (
        <Container
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 5,
                paddingTop: 8,
                paddingLeft: 3,
                paddingRight: 3,
            }}
        >
            <Button
                variant="text"
                startIcon={<ArrowBack />}
                sx={{
                    width: 'fit-content',
                    marginLeft: '-14px',
                    color: 'rgba(0, 0, 0, 0.87) !important',
                }}
                onClick={() => routerNavigate(-1)}
            >
                {t('Forms.Connections.Settings.Buttons.Back')}
            </Button>
            <HeaderWithDivider
                header={`${upperFirst(label || '')} ${t(
                    'Forms.Connections.Settings.Headline',
                )}`}
                Slot={
                    <Button
                        variant="text"
                        color={'warning'}
                        onClick={handleDeleteConnection}
                        disabled={loading}
                    >
                        {loading ? (
                            <CircularProgress
                                variant="indeterminate"
                                size="0.75rem"
                            />
                        ) : (
                            t('Forms.Connections.Settings.Buttons.Disconnect')
                        )}
                    </Button>
                }
            />
            <div>
                {operatorConnectionEnableTalk && isZendesk && (
                    <>
                        <ItemWithSwitch
                            description={t(
                                'Forms.Connections.Settings.TalkDescription',
                            )}
                            header={`${upperFirst(label || '')} ${t(
                                'Forms.Connections.Settings.Help',
                            )}`}
                            labelSwitch={`${upperFirst(label || '')} ${t(
                                'Forms.Connections.Settings.Help',
                            )}`}
                        />
                        <Divider
                            orientation="horizontal"
                            sx={{
                                width: '100%',
                            }}
                        />
                    </>
                )}
                {operatorConnectionEnableHelp && isZendesk && (
                    <ItemWithSwitch
                        description={t(
                            'Forms.Connections.Settings.HelpDescription',
                        )}
                        header={`${upperFirst(label || '')} ${t(
                            'Forms.Connections.Settings.Talk',
                        )}`}
                        labelSwitch={`${upperFirst(label || '')} ${t(
                            'Forms.Connections.Settings.Talk',
                        )}`}
                    />
                )}
            </div>
        </Container>
    );
}

export default ConnectionSettings;
